/*!
 * Copyright 2024 - Swiss Data Science Center (SDSC)
 * A partnership between École Polytechnique Fédérale de Lausanne (EPFL) and
 * Eidgenössische Technische Hochschule Zürich (ETHZ).
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License
 */

import cx from "classnames";
import { useEffect, useState } from "react";
import {
  generatePath,
  Navigate,
  Route,
  Routes,
  useMatch,
  useNavigate,
} from "react-router";

import ContainerWrap from "../../components/container/ContainerWrap";
import LazyNotFound from "../../not-found/LazyNotFound";
import {
  ABSOLUTE_ROUTES,
  RELATIVE_ROUTES,
} from "../../routing/routes.constants";
import useAppDispatch from "../../utils/customHooks/useAppDispatch.hook";
import useAppSelector from "../../utils/customHooks/useAppSelector.hook";
import { setFlag } from "../../utils/feature-flags/featureFlags.slice";
import LazyConnectedServicesPage from "../connectedServices/LazyConnectedServicesPage";
import LazyOAuthCompletePage from "../connectedServices/LazyOAuthCompletePage";
import GroupNew from "../groupsV2/new/GroupNew";
import LazyProjectV2ShowByProjectId from "../projectsV2/LazyProjectV2ShowByProjectId";
import ProjectV2New from "../projectsV2/new/ProjectV2New";
import { SearchEntity } from "../searchV2/api/searchV2Api.api";
import LazySearchV2 from "../searchV2/LazySearchV2";
import LazySecretsV2 from "../secretsV2/LazySecretsV2";
import useAppsEnabled from "../sessionsV2/apps/useAppsEnabled.hook";
import LazyAppLobbyPage from "../sessionsV2/LazyAppLobbyPage";
import LazySessionStartPage from "../sessionsV2/LazySessionStartPage";
import LazyShowSessionPage from "../sessionsV2/LazyShowSessionPage";
import LazyUserRedirect from "../usersV2/LazyUserRedirect";

function BetaV2Redirect() {
  const navigate = useNavigate();
  const betaProjectsMatch = useMatch(ABSOLUTE_ROUTES.v2.projects.beta.splat);
  const betaGroupsMatch = useMatch(ABSOLUTE_ROUTES.v2.groups.beta.splat);

  useEffect(() => {
    if (
      betaProjectsMatch?.params.namespace != null &&
      betaProjectsMatch?.params.slug != null
    ) {
      navigate(
        generatePath(ABSOLUTE_ROUTES.v2.projects.show.root, {
          namespace: betaProjectsMatch.params.namespace,
          slug: betaProjectsMatch.params.slug,
        }),
        { replace: true },
      );
    }
  }, [navigate, betaProjectsMatch?.params]);

  useEffect(() => {
    if (betaGroupsMatch?.params.slug != null) {
      navigate(
        generatePath(ABSOLUTE_ROUTES.v2.groups.show.root, {
          slug: betaGroupsMatch.params.slug,
        }),
        { replace: true },
      );
    }
  }, [navigate, betaGroupsMatch?.params]);
  return (
    <Navigate to={generatePath(ABSOLUTE_ROUTES.v2.index)} replace={true} />
  );
}

/**
 * @deprecated Use routing from react-router
 */
export default function RootV2() {
  const navigate = useNavigate();

  const { renku10Enabled } = useAppSelector(({ featureFlags }) => featureFlags);
  const dispatch = useAppDispatch();

  const [isFirstRender, setIsFirstRender] = useState(true);

  useEffect(() => {
    if (isFirstRender && !renku10Enabled) {
      dispatch(setFlag({ flag: "renku10Enabled", value: true }));
      return;
    }
    if (!renku10Enabled) {
      navigate("/");
    }
  }, [dispatch, isFirstRender, navigate, renku10Enabled]);

  useEffect(() => {
    // TODO: fix react-hooks/set-state-in-effect
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsFirstRender(false);
  }, []);

  return (
    <div className="w-100">
      <ProjectV2New />
      <GroupNew />

      <div className={cx("d-flex", "flex-grow-1")}>
        <Routes>
          <Route
            path={RELATIVE_ROUTES.v2.betaRoot}
            element={<BetaV2Redirect />}
          />
          <Route
            path={RELATIVE_ROUTES.v2.user}
            element={<LazyUserRedirect />}
          />
          <Route
            path={RELATIVE_ROUTES.v2.projects.root}
            element={<ProjectsV2Routes />}
          />
          <Route
            path={RELATIVE_ROUTES.v2.search}
            element={
              <ContainerWrap>
                <LazySearchV2 />
              </ContainerWrap>
            }
          />
          <Route
            path={RELATIVE_ROUTES.v2.integrations.root}
            element={
              <ContainerWrap>
                <LazyConnectedServicesPage />
              </ContainerWrap>
            }
          />
          <Route
            path={`${RELATIVE_ROUTES.v2.integrations.root}/${RELATIVE_ROUTES.v2.integrations.complete}`}
            element={<LazyOAuthCompletePage />}
          />
          <Route
            path={RELATIVE_ROUTES.v2.secrets}
            element={
              <ContainerWrap>
                <LazySecretsV2 />
              </ContainerWrap>
            }
          />
          <Route
            path="*"
            element={
              <ContainerWrap fullSize>
                <LazyNotFound />
              </ContainerWrap>
            }
          />
        </Routes>
      </div>
    </div>
  );
}

function RedirectToSearch({
  entityType,
}: {
  entityType: SearchEntity["type"];
}) {
  const navigate = useNavigate();
  useEffect(() => {
    navigate(
      {
        pathname: ABSOLUTE_ROUTES.v2.search,
        search: `type=${entityType}`,
      },
      { replace: true },
    );
  }, [entityType, navigate]);

  return null;
}

function ProjectsV2Routes() {
  return (
    <Routes>
      <Route index element={<RedirectToSearch entityType="Project" />} />
      <Route path={RELATIVE_ROUTES.v2.projects.show.root}>
        <Route
          path={RELATIVE_ROUTES.v2.projects.show.apps.root}
          element={<ProjectAppsRoutes />}
        />
        <Route
          path={RELATIVE_ROUTES.v2.projects.show.sessions.root}
          element={<ProjectSessionsRoutes />}
        />
        <Route
          path="*"
          element={
            <ContainerWrap fullSize>
              <LazyNotFound />
            </ContainerWrap>
          }
        />
      </Route>
      <Route
        path={RELATIVE_ROUTES.v2.projects.showById}
        element={<LazyProjectV2ShowByProjectId />}
      />
    </Routes>
  );
}

function ProjectAppsRoutes() {
  const appsEnabled = useAppsEnabled();

  if (!appsEnabled) {
    return (
      <ContainerWrap fullSize>
        <LazyNotFound />
      </ContainerWrap>
    );
  }

  return (
    <Routes>
      <Route
        path={RELATIVE_ROUTES.v2.projects.show.apps.show}
        element={
          <ContainerWrap fullSize>
            <LazyAppLobbyPage />
          </ContainerWrap>
        }
      />
      <Route
        path="*"
        element={
          <ContainerWrap fullSize>
            <LazyNotFound />
          </ContainerWrap>
        }
      />
    </Routes>
  );
}

function ProjectSessionsRoutes() {
  return (
    <Routes>
      <Route
        path="show/:session"
        element={
          <ContainerWrap fullSize>
            <LazyShowSessionPage />
          </ContainerWrap>
        }
      />
      <Route
        path=":launcherId/start"
        element={
          <ContainerWrap>
            <LazySessionStartPage />
          </ContainerWrap>
        }
      />
      <Route
        path="*"
        element={
          <ContainerWrap fullSize>
            <LazyNotFound />
          </ContainerWrap>
        }
      />
    </Routes>
  );
}
